import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Mail, HelpCircle, CreditCard, Sparkles, Shield, FileText, Clock, RefreshCw } from "lucide-react";

export default function FAQ() {
  const navigate = useNavigate();

  const faqs = [
    {
      category: "Getting Started",
      icon: <Sparkles className="w-5 h-5 text-blue-600" />,
      questions: [
        {
          q: "What is SmartReqs?",
          a: "SmartReqs is an AI-powered job requisition builder that helps HR professionals and hiring managers create professional, market-aligned job descriptions in minutes. It combines AI generation with real-time salary intelligence to produce complete requisitions ready for posting."
        },
        {
          q: "How does SmartReqs work?",
          a: "Simply enter a job title, location, and any specific requirements. Our AI analyzes market data from multiple sources and generates a complete job requisition including description, requirements, skills, qualifications, and competitive salary ranges."
        },
        {
          q: "Can I try SmartReqs before subscribing?",
          a: "Yes! Click 'Try SmartReqs Free' on our homepage to generate a sample requisition without creating an account. This lets you see exactly what you'll get before subscribing."
        },
        {
          q: "What industries does SmartReqs support?",
          a: "SmartReqs supports all industries including Healthcare, Technology, Finance, Aerospace & Defense, Retail, Energy, Construction, Manufacturing, and more. Our AI adapts to industry-specific terminology and requirements."
        }
      ]
    },
    {
      category: "Pricing & Subscription",
      icon: <CreditCard className="w-5 h-5 text-emerald-600" />,
      questions: [
        {
          q: "What subscription plans are available?",
          a: "We offer three plans: Monthly ($4/month), Quarterly ($10/3 months - save 17%), and Annual ($30/year - save 38%). All plans include unlimited requisition generation, salary intelligence, and export features."
        },
        {
          q: "Do subscriptions auto-renew?",
          a: "Yes, all subscriptions automatically renew at the end of each billing period. You can cancel anytime through your account settings, and you'll retain access until the end of your current billing period."
        },
        {
          q: "How do I cancel my subscription?",
          a: "Go to Settings in your dashboard, find the Subscription section, and click 'Cancel Subscription'. Your access continues until the end of your paid period."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processor."
        },
        {
          q: "Is there a refund policy?",
          a: "Subscription fees are non-refundable except as required by law. Since you can try SmartReqs free before subscribing, we encourage you to test the service first."
        }
      ]
    },
    {
      category: "Features & Capabilities",
      icon: <FileText className="w-5 h-5 text-purple-600" />,
      questions: [
        {
          q: "What's included in a generated requisition?",
          a: "Each requisition includes: job title, company description, detailed job description, key responsibilities, required qualifications, preferred qualifications, required skills, education requirements, experience requirements, and competitive salary range."
        },
        {
          q: "How accurate are the salary ranges?",
          a: "Our salary data is aggregated from multiple authoritative sources including the Bureau of Labor Statistics (BLS), major job boards, and industry benchmarks. Ranges are adjusted for location cost-of-living and experience level."
        },
        {
          q: "Can I edit the generated content?",
          a: "Absolutely! All generated content is fully editable. You can modify any section to match your specific needs, add company-specific information, or adjust requirements."
        },
        {
          q: "What export formats are available?",
          a: "You can export requisitions as PDF, Microsoft Word (.docx), or copy formatted text directly to your clipboard for pasting into your ATS or job posting platform."
        },
        {
          q: "What is the Requisition Library?",
          a: "The Library lets you save your best requisitions as templates. You can quickly duplicate and customize them for similar roles, saving time on recurring hiring needs."
        }
      ]
    },
    {
      category: "Account & Security",
      icon: <Shield className="w-5 h-5 text-amber-600" />,
      questions: [
        {
          q: "How do I create an account?",
          a: "Click 'Create Account' or choose a subscription plan on our Pricing page. Enter your company name, email, and password, then complete the secure checkout process."
        },
        {
          q: "How do I reset my password?",
          a: "Go to Settings in your dashboard and use the 'Change Password' section. You'll need your current password to set a new one. If you've forgotten your password, contact us at admin@smartreqs.ai."
        },
        {
          q: "Is my data secure?",
          a: "Yes. We use industry-standard encryption for all data transmission. Payment processing is handled by Stripe, a PCI-compliant payment processor. We never store your full credit card details."
        },
        {
          q: "Who can see my requisitions?",
          a: "Your requisitions are private to your account. We do not share, sell, or access your content except as necessary to provide the service."
        }
      ]
    },
    {
      category: "Technical & Support",
      icon: <HelpCircle className="w-5 h-5 text-red-600" />,
      questions: [
        {
          q: "What browsers are supported?",
          a: "SmartReqs works on all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version for the best experience."
        },
        {
          q: "Can I use SmartReqs on mobile devices?",
          a: "Yes, SmartReqs is responsive and works on tablets and smartphones. However, for the best experience when editing and exporting requisitions, we recommend using a desktop or laptop computer."
        },
        {
          q: "How do I contact support?",
          a: "Email us at admin@smartreqs.ai. We typically respond within 24-48 hours during business days."
        },
        {
          q: "What if the AI generates incorrect information?",
          a: "AI-generated content should always be reviewed before use. While our AI is trained on extensive job market data, you are responsible for verifying accuracy and making any necessary edits to match your specific requirements."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="faq-back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Frequently Asked Questions</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">How can we help?</h2>
          <p className="text-slate-600">
            Find answers to common questions about SmartReqs below. Can&apos;t find what you&apos;re looking for?{" "}
            <a href="mailto:admin@smartreqs.ai" className="text-blue-600 hover:underline">Contact us</a>.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqs.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-slate-100 rounded-lg">
                  {section.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900">{section.category}</h3>
              </div>
              
              <div className="space-y-4">
                {section.questions.map((faq, faqIndex) => (
                  <Card key={faqIndex} className="overflow-hidden">
                    <CardContent className="p-0">
                      <details className="group">
                        <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                          <span className="font-medium text-slate-900 pr-4">{faq.q}</span>
                          <span className="text-slate-400 group-open:rotate-180 transition-transform">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </summary>
                        <div className="px-4 pb-4 text-slate-600 border-t border-slate-100 pt-4">
                          {faq.a}
                        </div>
                      </details>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
          <p className="text-blue-100 mb-6">
            Our team is here to help. Reach out and we&apos;ll get back to you within 24-48 hours.
          </p>
          <a 
            href="mailto:admin@smartreqs.ai"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
          >
            <Mail className="w-5 h-5" />
            admin@smartreqs.ai
          </a>
        </div>

        {/* Quick Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Button variant="link" onClick={() => navigate("/terms-of-service")}>
            Terms of Service
          </Button>
          <Button variant="link" onClick={() => navigate("/privacy-policy")}>
            Privacy Policy
          </Button>
          <Button variant="link" onClick={() => navigate("/pricing")}>
            Pricing
          </Button>
          <Button variant="link" onClick={() => navigate("/try")}>
            Try SmartReqs
          </Button>
        </div>
      </main>
    </div>
  );
}
